import { defineStore } from 'pinia'
import { useCompany } from '@/store/company'
import { useWallets } from '@/store/wallets'
import { useReports } from '@/store/reports'
import { useNotifications } from '@/store/notifications'
import { useFeatures } from '@/store/features'
import { useSockets } from '@/libs/composables/sockets'
import { useAuthSessions } from '@/store/authSessions.js'
import { useBeneficiaries } from '@/store/beneficiaries.js'
import { useUser } from '@/store/user'
import { useSettings } from '@/store/settings'
import { useOob } from '@/store/oob'

const taskDefinitions = {
  profile: { load: () => useUser().getUser() },
  settings: { load: () => useSettings().getSettings(useUser().region) },
  company: { load: () => useCompany().init() },
  wallets: {
    load: () => useWallets().getList(),
    getProgress: () => useWallets().loadProgress,
  },
  beneficiaries: {
    load: () => useBeneficiaries().getList(),
    getProgress: () => useBeneficiaries().loadProgress,
  },
  reports: { load: () => useReports().getReports() },
  features: { load: () => useFeatures().getFeatures() },
  sockets: { load: () => useSockets().init() },
  notifications: { load: () => useNotifications().getNotificationsInfo() },
  sessions: { load: () => useAuthSessions().getSessions() },
  approvals: { load: () => useOob().fetchRequests() },
}

// Promises coordinate requests; only task statuses belong in reactive state.
const taskPromises = new WeakMap()
const bootstrapPromises = new WeakMap()

export const useBootstrap = defineStore('bootstrap', {
  state: () => ({
    isReady: false,
    error: null,
    startedAt: 0,
    tasks: Object.keys(taskDefinitions).map((id) => ({
      id,
      status: 'pending',
    })),
  }),
  getters: {
    // Keep task execution state separate from live pagination in domain stores.
    progressTasks: (state) =>
      state.tasks.map((task) => {
        const { getProgress } = taskDefinitions[task.id]
        return getProgress ? { ...task, progress: getProgress() } : task
      }),
  },
  actions: {
    runTask(task) {
      if (task.status === 'done') return Promise.resolve()
      if (taskPromises.has(task)) return taskPromises.get(task)

      task.status = 'loading'
      const promise = Promise.resolve()
        .then(taskDefinitions[task.id].load)
        .then(() => {
          task.status = 'done'
        })
        .catch((error) => {
          task.status = 'error'
          throw error
        })
        .finally(() => taskPromises.delete(task))
      taskPromises.set(task, promise)
      return promise
    },
    initialize() {
      if (this.isReady) return Promise.resolve()
      const tasks = this.tasks
      if (bootstrapPromises.has(tasks)) return bootstrapPromises.get(tasks)

      this.error = null
      this.startedAt = Date.now()
      const promise = Promise.resolve().then(async () => {
        try {
          // Settings depend on the profile; the remaining tasks run together.
          for (const task of tasks.slice(0, 2)) {
            await this.runTask(task)
            if (this.tasks !== tasks) return
          }
          await Promise.all(tasks.slice(2).map((task) => this.runTask(task)))
          if (this.tasks === tasks) this.isReady = true
        } catch (error) {
          if (this.tasks === tasks) this.error = error
          throw error
        } finally {
          bootstrapPromises.delete(tasks)
        }
      })
      bootstrapPromises.set(tasks, promise)
      return promise
    },
  },
})
